import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Box,
  Stack,
  Chip,
  Button,
  TextField,
  InputAdornment,
  Avatar,
  Badge,
  Tabs,
  Tab,
  IconButton,
  Menu,
  MenuItem,
  Pagination,
  useTheme,
  Skeleton
} from '@mui/material';
import {
  Search,
  FilterList,
  MoreVert,
  Visibility,
  Favorite,
  Share,
  LocationOn,
  Schedule,
  Person,
  TrendingUp,
  FlashOn,
  Star,
  Facebook,
  Instagram,
  YouTube
} from '@mui/icons-material';
import annonceService from '../services/annonceService';


const Annonces = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [anchorEl, setAnchorEl] = useState(null);
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(21);
  const theme = useTheme();


  // Fonction pour charger les annonces
  const loadAds = async () => {
    try {
      setLoading(true);
      setError('');
      // console.log('Loading ads...');
      const data = await annonceService.getAds({ 
        limit: 10000 
      });
      // console.log('Ads loaded:', data);
      setAds(data.ads || []);
    } catch (err) {
      setError('Erreur lors du chargement des annonces');
      console.error('Error loading ads:', err);
      console.error('Error details:', err.response?.data || err.message);
    } finally {
      setLoading(false);
    }
  };

  // Charger les annonces au montage du composant
  useEffect(() => {
    loadAds();
  }, []);

  // Fonction pour filtrer et trier les annonces
  const getFilteredAnnonces = () => {
    let filtered = ads.filter(ad => ad.isActive);
    
    // Filtrage par recherche
    if (searchQuery) {
      filtered = filtered.filter(ad => 
        ad.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (ad.description && ad.description.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }
    
    // Tri par ordre décroissant (plus récent en premier)
    filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    
    return filtered;
  };

  const filteredAnnonces = getFilteredAnnonces();

  // Calcul de la pagination
  const totalPages = Math.ceil(filteredAnnonces.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentAnnonces = filteredAnnonces.slice(startIndex, endIndex);

  // Fonction pour changer de page
  const handlePageChange = (event, page) => {
    setCurrentPage(page);
    // Scroll vers le haut de la page
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Fonction pour gérer les clics sur les réseaux sociaux
  const handleSocialMediaClick = (url) => {
    if (url) {
      let socialUrl = url;

      // Si l'URL ne commence pas par http:// ou https://, on ajoute https://
      if (!socialUrl.startsWith('http://') && !socialUrl.startsWith('https://')) {
        socialUrl = 'https://' + socialUrl;
      }

      window.open(socialUrl, '_blank', 'noopener,noreferrer');
    }
  };

  // Reset de la page quand on change de recherche ou catégorie
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedCategory]);

  const AnnonceSkeleton = () => (
    <Card>
      <Skeleton variant="rectangular" height={200} />
      <CardContent>
        <Skeleton variant="text" height={32} />
        <Skeleton variant="text" height={20} />
        <Skeleton variant="text" height={20} />
        <Skeleton variant="rectangular" height={36} sx={{ mt: 2 }} />
      </CardContent>
    </Card>
  );

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header with Search and Filters */}
      <Box sx={{ mb: 6 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'flex-start', md: 'center' },
          justifyContent: 'space-between',
          gap: 3,
          mb: 2
        }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h2" gutterBottom sx={{ fontWeight: 700 }}>
              Annonces
            </Typography>
            {/* <Typography variant="h6" color="text.secondary">
              Découvrez les meilleures offres de nos vendeurs partenaires
            </Typography> */}
          </Box>
          
          {/* Search and Filters */}
          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            spacing={2} 
            alignItems="center"
            sx={{ 
              width: { xs: '100%', md: 'auto' },
              minWidth: { md: 400 }
            }}
          >
            <TextField
              placeholder="Rechercher une annonce..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                )
              }}
              sx={{ 
                flexGrow: 1, 
                maxWidth: { xs: '100%', sm: 400 },
                width: { xs: '100%', sm: 'auto' }
              }}
            />
            {/* <IconButton 
              onClick={handleMenuClick}
              sx={{
                bgcolor: 'action.hover',
                '&:hover': { bgcolor: 'action.selected' }
              }}
            >
              <FilterList />
            </IconButton> */}
          </Stack>
        </Box>
      </Box>

      {/* Results Header */}
      {filteredAnnonces.length > 0 && (
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            {filteredAnnonces.length} annonce{filteredAnnonces.length > 1 ? 's' : ''} trouvée{filteredAnnonces.length > 1 ? 's' : ''}
            {totalPages > 1 && (
                      <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                        (Page {currentPage} sur {totalPages})
                      </Typography>
                    )}
          </Typography>
          {/* <Stack direction="row" spacing={1}>
            <Chip label="Nouveautés" color="primary" variant="outlined" />
            <Chip label="Prix réduits" variant="outlined" />
            <Chip label="Vendeurs vérifiés" variant="outlined" />
          </Stack> */}
        </Box>
      )}

      {/* Annonces Grid */}
      {loading ? (
        <Grid container spacing={3} sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gridAutoRows: '1fr'
        }}>
          {[...Array(9)].map((_, index) => (
            <Grid item xs={12} sm={6} lg={3} key={index}>
              <AnnonceSkeleton />
            </Grid>
          ))}
        </Grid>
      ) : error ? (
        <Box textAlign="center" sx={{ py: 8 }}>
          <Typography variant="h6" color="error" gutterBottom>
            {error}
          </Typography>
        </Box>
      ) : filteredAnnonces.length === 0 ? (
        <Box textAlign="center" sx={{ py: 8 }}>
          <Typography variant="h5" color="text.secondary" gutterBottom>
            Aucune annonce trouvée
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Essayez de modifier vos critères de recherche
          </Typography>
        </Box>
      ) : (
        <>
          <Grid container spacing={3} sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gridAutoRows: '1fr'
          }}>
            {currentAnnonces.map((annonce) => (
              <Grid item xs={12} sm={6} lg={3} key={annonce.id}>
            <Card 
              sx={{ 
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-1px)',
                  boxShadow: 2
                }
              }}
            >
              <Box sx={{ position: 'relative' }}>
                {(() => {
                  // Gérer la migration depuis imageUrl vers images
                  const hasImages = annonce.images && Array.isArray(annonce.images) && annonce.images.length > 0;
                  const hasImageUrl = annonce.imageUrl;
                  const hasVideo = annonce.video;
                  
                  // Si pas d'image mais une vidéo, afficher la vidéo
                  if (!hasImages && !hasImageUrl && hasVideo) {
                    return (
                      <CardMedia
                        component="video"
                        height="320"
                        src={annonce.video}
                        sx={{ 
                          objectFit: 'content',
                          width: '100%',
                          height: '320px'
                        }}
                        controls
                        muted={false}
                        loop={false}
                        playsInline
                      />
                    );
                  }
                  
                  // Sinon, afficher l'image (ou l'image par défaut seulement si pas de vidéo)
                  return (
                    <CardMedia
                      component="img"
                      height="320"
                      image={
                        hasImages 
                          ? annonce.images[0]
                          : (hasImageUrl || (!hasVideo ? '/img/no_media.jpg' : null))
                      }
                      alt={annonce.title}
                      sx={{ objectFit: 'content', cursor: 'pointer' }}
                      onClick={() => navigate(`/annonces/${annonce.id}`)}
                    />
                  );
                })()}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 8,
                    left: 8,
                    display: 'flex',
                    gap: 1
                  }}
                >
                  {/* {annonce.isActive && (
                    <Chip 
                      label="Active" 
                      color="success" 
                      size="small" 
                    />
                  )} */}
                </Box>
                <Box
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    display: 'flex',
                    gap: 1
                  }}
                >
                  {/* <IconButton
                    size="small"
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.9)',
                      '&:hover': { bgcolor: 'white' }
                    }}
                  >
                    <Favorite fontSize="small" />
                  </IconButton> */}
                  {/* <IconButton
                    size="small"
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.9)',
                      '&:hover': { bgcolor: 'white' }
                    }}
                  >
                    <Share fontSize="small" />
                  </IconButton> */}
                </Box>
                {annonce.originalPrice && (
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 8,
                      right: 8,
                      bgcolor: 'error.main',
                      color: 'white',
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      fontSize: '0.75rem',
                      fontWeight: 'bold'
                    }}
                  >
                    -{Math.round((1 - annonce.price / annonce.originalPrice) * 100)}%
                  </Box>
                )}
              </Box>

              <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                 <Typography 
                   variant="h6" 
                   gutterBottom 
                   sx={{ 
                     fontWeight: 600, 
                     cursor: 'pointer',
                     '&:hover': { color: 'primary.main' }
                   }}
                   onClick={() => navigate(`/annonces/${annonce.id}`)}
                 >
                  {annonce.title}
                </Typography>
                
                {annonce.description && (
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  paragraph
                  sx={{ 
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    flexGrow: 1
                  }}
                >
                  {annonce.description}
                </Typography>
                )}

                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                  <Box sx={{ flexGrow: 1 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Schedule sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        {new Date(annonce.createdAt).toLocaleDateString()}
                      </Typography>
                    </Stack>
                  </Box>
                </Stack>

                 <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 2 }}>
                   <Button 
                     variant="contained" 
                     size="small" 
                     fullWidth
                     sx={{
                       background: theme.palette.color.degradeOne,
                       color: 'white',
                       '&:hover': {
                         background: theme.palette.color.degradeOne,
                         opacity: 0.9
                       }
                     }}
                     onClick={() => navigate(`/annonces/${annonce.id}`)}
                   >
                     Voir l'annonce
                  </Button>
                </Box>

                {/* Réseaux sociaux */}
                {(annonce.facebookUrl || annonce.instagramUrl || annonce.snapchatUrl || annonce.tiktokUrl || annonce.youtubeUrl) && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                    {annonce.facebookUrl && (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSocialMediaClick(annonce.facebookUrl);
                        }}
                        sx={{
                          color: '#1877F2',
                          '&:hover': { bgcolor: 'rgba(24, 119, 242, 0.1)' }
                        }}
                        title="Facebook"
                      >
                        <Facebook fontSize="small" />
                      </IconButton>
                    )}
                    {annonce.instagramUrl && (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSocialMediaClick(annonce.instagramUrl);
                        }}
                        sx={{
                          color: '#E4405F',
                          '&:hover': { bgcolor: 'rgba(228, 64, 95, 0.1)' }
                        }}
                        title="Instagram"
                      >
                        <Instagram fontSize="small" />
                      </IconButton>
                    )}
                    {annonce.snapchatUrl && (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSocialMediaClick(annonce.snapchatUrl);
                        }}
                        sx={{
                          '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.1)' }
                        }}
                        title="Snapchat"
                      >
                        <Box
                          component="img"
                          src="/img/snapchat-brands-solid-full.svg"
                          alt="Snapchat"
                          sx={{
                            width: 20,
                            height: 20,
                            objectFit: 'contain'
                          }}
                        />
                      </IconButton>
                    )}
                    {annonce.tiktokUrl && (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSocialMediaClick(annonce.tiktokUrl);
                        }}
                        sx={{
                          '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.1)' }
                        }}
                        title="TikTok"
                      >
                        <Box
                          component="img"
                          src="/img/tiktok-brands-solid-full.svg"
                          alt="TikTok"
                          sx={{
                            width: 20,
                            height: 20,
                            objectFit: 'contain'
                          }}
                        />
                      </IconButton>
                    )}
                    {annonce.youtubeUrl && (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSocialMediaClick(annonce.youtubeUrl);
                        }}
                        sx={{
                          color: '#FF0000',
                          '&:hover': { bgcolor: 'rgba(255, 0, 0, 0.1)' }
                        }}
                        title="YouTube"
                      >
                        <YouTube fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
              </Grid>
            ))}
          </Grid>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              mt: 4, 
              mb: 2 
            }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
                siblingCount={1}
                boundaryCount={1}
                sx={{
                  '& .MuiPaginationItem-root': {
                    fontSize: '1rem',
                    fontWeight: 500
                  }
                }}
              />
            </Box>
          )}
        </>
      )}

      {/* Filter Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem>Trier par prix croissant</MenuItem>
        <MenuItem>Trier par prix décroissant</MenuItem>
        <MenuItem>Trier par date</MenuItem>
        <MenuItem>Trier par popularité</MenuItem>
      </Menu>
    </Container>
  );
};

export default Annonces;
